import { AfterViewInit, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { DataTableDirective } from 'angular-datatables';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Observable, Subject } from 'rxjs';
import { DocumentosElectronicosService } from 'src/app/services/transacciones/documentos-electronicos.service';
import { LanguageApp } from '../../laboratorio/laboratorios/laboratorios.component';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from 'src/app/authentication/auth.service';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-documentos-electronicos',
  templateUrl: './documentos-electronicos.component.html',
  styleUrls: ['./documentos-electronicos.component.scss'],
  standalone: false
})
export class DocumentosElectronicosComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(DataTableDirective, { static: false }) dtElement!: DataTableDirective;
  @ViewChild('rideModal', { static: false }) rideModal?: TemplateRef<any>;
  @ViewChild('ticketModal', { static: false }) ticketModal?: TemplateRef<any>;
  @ViewChild('notaCreditoModal', { static: false }) notaCreditoModal?: TemplateRef<any>;

  dtOptions: DataTables.Settings = {};
  dtTrigger: Subject<any> = new Subject();
  isDtInitialized = false;

  public documentos: any[] = [];
  public spinnerBoolean = false;
  public fechaInicio: string = '';
  public fechaFin: string = '';
  public generandoRide = false;
  public isMobileView = false;
  public rideUrl: SafeResourceUrl = '';
  public rideModalRef?: NgbModalRef;
  public ticketUrl: SafeResourceUrl = '';
  public ticketModalRef?: NgbModalRef;
  public notaCreditoModalRef?: NgbModalRef;
  public documentoNC: any = null;
  public valorDevolver: number = 0;
  public razonModificacion: string = '';
  public generandoNC = false;
  public idUsuario: string = '';
  public nombreUsuario: string = '';
  public enviandoSRI: { [codigo: string]: boolean } = {};

  private mobileTablesObserver?: MutationObserver;
  private mobileLabelsRaf?: number;
  private mobileDefaultApplied = false;

  get token(): string { return localStorage.getItem('token') || ''; }

  constructor(
    private documentosElectronicosService: DocumentosElectronicosService,
    private ngZone: NgZone,
    private toastr: ToastrService,
    private elementRef: ElementRef,
    private modalService: NgbModal,
    private sanitizer: DomSanitizer,
    private authService: AuthService,
  ) {}

  @HostListener('window:resize')
  onWindowResize(): void {
    this.detectMobileView();
    this.updateMobileTableLabels();
  }

  private detectMobileView(): void {
    if (typeof window === 'undefined') {
      this.isMobileView = false;
      return;
    }
    const previousValue = this.isMobileView;
    this.isMobileView = window.innerWidth <= 992;

    if (this.isMobileView && !this.mobileDefaultApplied) {
      this.mobileDefaultApplied = true;
    } else if (!this.isMobileView && previousValue !== this.isMobileView) {
      this.mobileDefaultApplied = false;
    }
  }

  ngOnInit(): void {
    const usuarioActual = this.authService.loginUsuario;
    this.idUsuario = usuarioActual.idUsuario || '';
    this.nombreUsuario = usuarioActual.nombre || '';
    // Set initial date filters to today in Ecuador timezone
    const hoy = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guayaquil' });
    this.fechaInicio = hoy;
    this.fechaFin = hoy;
    this.detectMobileView();
    this.buildDtOptions();
  }

  ngAfterViewInit(): void {
    this.dtTrigger.next(null);
    this.isDtInitialized = true;
    this.initializeMobileTables();
  }

  ngOnDestroy(): void {
    this.dtTrigger.unsubscribe();
    this.mobileTablesObserver?.disconnect();
    if (this.mobileLabelsRaf) {
      cancelAnimationFrame(this.mobileLabelsRaf);
    }
  }

  private initializeMobileTables(): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.updateMobileTableLabels();

    this.ngZone.runOutsideAngular(() => {
      if (typeof MutationObserver === 'undefined') {
        return;
      }

      this.mobileTablesObserver?.disconnect();
      this.mobileTablesObserver = new MutationObserver(() => this.updateMobileTableLabels());
      this.mobileTablesObserver.observe(this.elementRef.nativeElement, { childList: true, subtree: true });
    });
  }

  private updateMobileTableLabels(): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (this.mobileLabelsRaf) {
      cancelAnimationFrame(this.mobileLabelsRaf);
    }

    this.mobileLabelsRaf = window.requestAnimationFrame(() => {
      const hostElement = this.elementRef.nativeElement as HTMLElement;
      const tables = Array.from(hostElement.querySelectorAll<HTMLTableElement>('.mobile-friendly-table'));

      tables.forEach((table) => {
        const headers = Array.from(table.querySelectorAll<HTMLTableCellElement>('thead th')).map((th) => th.textContent?.trim() ?? '');

        table.querySelectorAll('tbody tr').forEach((row) => {
          row.querySelectorAll<HTMLTableCellElement>('td').forEach((cell, index) => {
            const headerText = headers[index];
            if (headerText && cell.getAttribute('data-label') !== headerText) {
              cell.setAttribute('data-label', headerText);
            }
          });
        });
      });
    });
  }

  aplicarFiltros(): void {
    if (this.isDtInitialized) {
      this.dtElement?.dtInstance?.then(dt => dt.ajax.reload());
    }
  }

  private buildDtOptions(): void {
    this.dtOptions = {
      language: LanguageApp.spanish_datatables,
      pagingType: 'full_numbers',
      pageLength: 10,
      serverSide: true,
      processing: true,
      responsive: true,
      orderClasses: false,
      searchDelay: 1500,
      ajax: (dtParams: any, callback: any) => {
        this.ngZone.run(() => { this.documentos = []; this.spinnerBoolean = true; });

        this.documentosElectronicosService.listarDocumentos(this.token, {
          fechaInicio: this.fechaInicio,
          fechaFin:    this.fechaFin,
          start:       dtParams.start,
          length:      dtParams.length,
          search:      dtParams.search?.value || '',
        }).subscribe((resp: any) => {
          this.ngZone.run(() => {
            this.documentos = Array.isArray(resp?.data) ? resp.data : [];
            this.spinnerBoolean = false;
            callback({
              recordsTotal:    resp?.recordsTotal    || 0,
              recordsFiltered: resp?.recordsFiltered || 0,
              data: [],
            });
            this.updateMobileTableLabels();
          });
        });
      },
    };
  }

  formatFecha(fecha: string): string {
    if (!fecha) return '';
    try {
      const d   = new Date(fecha);
      const TZ  = 'America/Guayaquil';
      const dia = d.toLocaleDateString('en-CA', { timeZone: TZ }).split('-').reverse().join('/');
      const hora = d.toLocaleTimeString('en-GB', { timeZone: TZ, hour12: false }).slice(0, 5);
      return `${dia} ${hora}`;
    } catch { return fecha; }
  }

  getEstadoBadgeClass(estado: string): string {
    const map: { [key: string]: string } = {
      'activo': 'bg-success',
      'pendiente': 'bg-warning',
      'anulado': 'bg-danger'
    };
    return map[estado?.toLowerCase()] || 'bg-secondary';
  }

  getEstadoLabel(estado: string): string {
    const map: { [key: string]: string } = {
      'activo': 'Autorizado',
      'pendiente': 'Pendiente',
      'anulado': 'Anulado'
    };
    return map[estado?.toLowerCase()] || estado;
  }

  getNotaCreditoBadgeClass(estadoNota: any): string {
    return estadoNota ? 'badge bg-success' : 'badge bg-secondary';
  }

  extraerNombreDoctor(obsTrac: any): string {
    if (!obsTrac) return 'N/A';

    try {
      const parsed = JSON.parse(obsTrac);
      return parsed.doctor || parsed.nombreDoctor || parsed.NOM_DOC || 'N/A';
    } catch (e) {
      return obsTrac || 'N/A';
    }
  }

  shouldShowCheck(campo: string, doc: any): boolean {
    if (campo === 'enviadoCliente') {
      const count = [doc.firmado, doc.enviado, doc.autorizado, doc.enviadoCliente].filter(v => v === '1' || v === 1).length;
      return count >= 3 || doc.enviadoCliente === '1' || doc.enviadoCliente === 1;
    }
    return doc[campo] === '1' || doc[campo] === 1;
  }

  puedeEnviarSRI(documento: any): boolean {
    return !!documento?.claveFactura && !this.shouldShowCheck('autorizado', documento);
  }

  estaEnviandoSRI(documento: any): boolean {
    return !!this.enviandoSRI[String(documento?.COD_TRAC || '')];
  }

  enviarDocumentoSRI(documento: any): void {
    if (!documento?.claveFactura) {
      this.toastr.error('No se encontró clave de acceso');
      return;
    }

    const codigoDocumento = String(documento.COD_TRAC || '');
    if (this.enviandoSRI[codigoDocumento]) {
      return;
    }

    this.enviandoSRI[codigoDocumento] = true;
    this.spinnerBoolean = true;
    const emailCliente = documento.email_cliente || documento.EMA_CLI || '';
    const nombreCliente = documento.nombre_cliente?.trim() ? documento.nombre_cliente : documento.NOM_CLI;

    this.documentosElectronicosService.enviarNotaCreditoSRI(
      documento.claveFactura,
      emailCliente,
      nombreCliente || '',
      this.token
    ).subscribe({
      next: (resp: any) => {
        this.spinnerBoolean = false;
        delete this.enviandoSRI[codigoDocumento];
        const isOldFormat = resp && typeof resp === 'object' &&
          resp['7'] && typeof resp['7'] === 'string' &&
          resp['7'].toLowerCase().includes('mail');
        const success = resp?.success === true || isOldFormat;

        if (success) {
          this.toastr.success('Documento enviado al SRI correctamente');
        } else {
          this.toastr.warning('El SRI no autorizó todavía el documento');
        }

        this.recargarTabla();
      },
      error: (err) => {
        this.spinnerBoolean = false;
        delete this.enviandoSRI[codigoDocumento];
        this.toastr.error('No se pudo enviar al SRI: ' + (err?.error?.error || err?.error?.message || 'Error desconocido'));
      }
    });
  }

  private recargarTabla(): void {
    if (this.isDtInitialized) {
      this.dtElement?.dtInstance?.then(dt => dt.ajax.reload());
    }
  }

  generarRide(documento: any): void {
    if (!documento?.claveFactura) {
      this.toastr.error('No se encontró clave de acceso');
      return;
    }

    this.generandoRide = true;
    this.documentosElectronicosService.generarRide(this.token, documento.claveFactura, documento.estadoFactura)
      .subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);

          if (this.isMobileView && this.rideModal) {
            this.rideUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
            this.rideModalRef = this.modalService.open(this.rideModal, {
              size: 'lg',
              centered: true,
              scrollable: true
            });
          } else {
            window.open(url, '_blank');
          }

          this.generandoRide = false;
          this.toastr.success('RIDE generado correctamente');
        },
        error: (err) => {
          this.generandoRide = false;
          this.toastr.error('Error al generar el RIDE: ' + (err?.error?.error || 'desconocido'));
          console.error(err);
        }
      });
  }

  abrirDocumentoTabla(documento: any): void {
    if (!documento?.ordenFactura) {
      this.toastr.error('No se encontró número de orden');
      return;
    }

    this.abrirTicketNode(
      this.documentosElectronicosService.generarReciboInterno(this.token, {
        numeroFactura: String(documento.NUM_TRAC || ''),
        tipoFactura: 'normal',
        idOrden: String(documento.ordenFactura)
      })
    );
  }

  abrirFacturaPdf(documento: any): void {
    if (!documento?.ordenFactura) {
      this.toastr.error('No se encontró número de orden');
      return;
    }

    this.abrirTicketNode(
      this.documentosElectronicosService.generarFacturaCliente(this.token, {
        numeroFactura: String(documento.ordenFactura),
        tipoFactura: 'normal',
        idOrden: String(documento.ordenFactura)
      })
    );
  }

  private abrirTicketNode(request$: Observable<Blob>): void {
    request$.subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        if (this.isMobileView && this.ticketModal) {
          this.ticketUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
          this.ticketModalRef = this.modalService.open(this.ticketModal, {
            size: 'lg',
            centered: true,
            scrollable: true
          });
        } else {
          window.open(url, '_blank');
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      },
      error: (err) => {
        this.toastr.error('No se pudo abrir el documento: ' + (err?.error?.message || err?.message || 'Error desconocido'));
      }
    });
  }

  exportarExcel(): void {
    this.spinnerBoolean = true;
    this.documentosElectronicosService.exportarExcel(this.token, {
      fechaInicio: this.fechaInicio,
      fechaFin: this.fechaFin,
    }).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `documentos-electronicos-${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.spinnerBoolean = false;
        this.toastr.success('Excel exportado correctamente');
      },
      error: (err) => {
        this.spinnerBoolean = false;
        this.toastr.error('Error al exportar: ' + (err?.error?.error || 'desconocido'));
        console.error(err);
      }
    });
  }

  abrirModalNotaCredito(documento: any): void {
    if (documento.estadoNota) {
      this.toastr.error('Este documento ya tiene una nota de crédito registrada');
      return;
    }

    // No permitir nota de crédito si es facturado a CONSUMIDOR FINAL
    if (documento.NOM_CLI === 'CONSUMIDOR FINAL') {
      this.toastr.error('No se puede crear nota de crédito para CONSUMIDOR FINAL');
      return;
    }

    this.documentoNC = documento;
    this.valorDevolver = documento.TOTPAG_TRAC;
    this.razonModificacion = '';

    this.notaCreditoModalRef = this.modalService.open(this.notaCreditoModal, {
      size: 'lg',
      centered: true,
      backdrop: 'static'
    });
  }

  generarNotaCredito(): void {
    if (!this.razonModificacion || this.razonModificacion.trim().length === 0) {
      this.toastr.error('Ingrese la razón de la modificación');
      return;
    }

    this.generandoNC = true;

    this.documentosElectronicosService.crearNotaCredito(this.token, {
      numero: btoa(this.documentoNC.COD_TRAC),
      valorDevolver: this.valorDevolver,
      razonModificacion: this.razonModificacion,
      idUsuario: this.idUsuario,
      nombreUsuario: this.nombreUsuario,
    }).subscribe({
      next: (resp: any) => {
        if (resp.success) {
          // Enviar nota de crédito al SRI con los datos devueltos
          this.documentosElectronicosService.enviarNotaCreditoSRI(
            resp.claveAcceso,
            resp.emailCliente,
            resp.nombreCliente,
            this.token
          ).subscribe({
            next: (respSRI: any) => {
              this.generandoNC = false;

              // Detectar si la respuesta es el formato antiguo (array con índices numéricos)
              const isOldFormat = respSRI && typeof respSRI === 'object' &&
                                 respSRI['7'] && typeof respSRI['7'] === 'string' &&
                                 respSRI['7'].toLowerCase().includes('mail');

              // Detectar si fue exitoso (nuevo formato o antiguo formato)
              const success = respSRI?.success === true || isOldFormat;

              if (success) {
                this.toastr.success('Nota de crédito correctamente enviada');
              } else {
                this.toastr.warning('Nota de crédito generada pero hubo error enviando al SRI');
              }

              this.notaCreditoModalRef?.dismiss();
              setTimeout(() => {
                if (this.isDtInitialized) {
                  this.dtElement?.dtInstance?.then(dt => dt.ajax.reload());
                }
              }, 1000);
            },
            error: (errSRI: any) => {
              this.generandoNC = false;
              this.toastr.warning('Nota de crédito generada pero hubo error enviando al SRI. ' + (errSRI?.error?.error || 'Error desconocido'));
              this.notaCreditoModalRef?.dismiss();
              setTimeout(() => {
                if (this.isDtInitialized) {
                  this.dtElement?.dtInstance?.then(dt => dt.ajax.reload());
                }
              }, 1000);
            }
          });
        } else {
          this.generandoNC = false;
          this.toastr.error('Error al generar nota: ' + (resp.error || 'Error desconocido'));
        }
      },
      error: (err) => {
        this.generandoNC = false;
        this.toastr.error('Error: ' + (err?.error?.error || err?.error?.message || 'desconocido'));
        console.error(err);
      }
    });
  }
}
